import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX, FiChevronRight, FiChevronLeft } from "react-icons/fi";
import "../styles/CatalogSheet.css";
import {
  apiCatalogMenu,
  apiCatalogChildren,
  apiCatalogProducts,
} from "../api/catalog.api";
import CatalogProductGrid from "../components/catalog/CatalogProductGrid";
import AlternativeProductsBlock from "../components/AlternativeProductsBlock";

const ANIM_MS = 450;
const CLOSE_DRAG_THRESHOLD = 120;
const MAX_DRAG_Y = 420;

export default function CatalogSheet({
  open,
  onClose,
  topOffset = 84,
  onOpenProduct,
  onAction,
}) {
  const { t, i18n } = useTranslation("catalog");

  const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("en")
    ? "en"
    : "uk";

  const sheetRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragDeltaYRef = useRef(0);
  const draggingRef = useRef(false);

  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stack, setStack] = useState([]);
  const [mode, setMode] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [dragY, setDragY] = useState(0);

  const title = useMemo(() => {
    if (!stack.length) return t("title");
    return stack[stack.length - 1]?.title || t("title");
  }, [stack, t]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      setDragY(0);
      draggingRef.current = false;
      dragDeltaYRef.current = 0;
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        setDragY(0);
      }, ANIM_MS);
      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }

    if (mounted) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const top = await apiCatalogMenu(lang);
        if (!alive) return;

        setStack([]);
        setMode("categories");
        setCategories(top || []);
        setProducts([]);
      } catch (e) {
        console.warn("catalog menu error:", e?.message);
        if (!alive) return;

        setStack([]);
        setMode("categories");
        setCategories([]);
        setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, lang]);

  async function openCategory(cat) {
    if (!cat?.id) return;

    setLoading(true);
    try {
      const children = await apiCatalogChildren(cat.id, lang);

      setStack((s) => [...s, { id: String(cat.id), title: cat.title }]);

      if (children?.length) {
        setMode("categories");
        setCategories(children);
        setProducts([]);
      } else {
        const prods = await apiCatalogProducts(cat.id, lang);
        setMode("products");
        setProducts(prods || []);
        setCategories([]);
      }
    } catch (e) {
      console.warn("openCategory error:", e?.message);
    } finally {
      setLoading(false);
    }
  }

  async function goBack() {
    if (!stack.length) return;

    const nextStack = stack.slice(0, -1);
    setStack(nextStack);

    setLoading(true);
    try {
      if (!nextStack.length) {
        const top = await apiCatalogMenu(lang);
        setMode("categories");
        setCategories(top || []);
        setProducts([]);
        return;
      }

      const parent = nextStack[nextStack.length - 1];
      const children = await apiCatalogChildren(parent.id, lang);

      if (children?.length) {
        setMode("categories");
        setCategories(children);
        setProducts([]);
      } else {
        const prods = await apiCatalogProducts(parent.id, lang);
        setMode("products");
        setProducts(prods || []);
        setCategories([]);
      }
    } catch (e) {
      console.warn("goBack error:", e?.message);
    } finally {
      setLoading(false);
    }
  }

  function handleProductBlockAction(a) {
    if (a?.type === "open_product") {
      return onOpenProduct?.(a.product);
    }
    return onAction?.(a);
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  function handleDragStart(clientY) {
    if (closing) return;
    draggingRef.current = true;
    dragStartYRef.current = clientY;
    dragDeltaYRef.current = 0;
  }

  function handleDragMove(clientY) {
    if (!draggingRef.current) return;

    const delta = clientY - dragStartYRef.current;
    const next = clamp(delta, 0, MAX_DRAG_Y);

    dragDeltaYRef.current = next;
    setDragY(next);
  }

  function handleDragEnd() {
    if (!draggingRef.current) return;

    draggingRef.current = false;
    const shouldClose = dragDeltaYRef.current > CLOSE_DRAG_THRESHOLD;

    if (shouldClose) {
      setDragY(0);
      onClose?.();
      return;
    }

    dragDeltaYRef.current = 0;
    setDragY(0);
  }

  function onPointerDown(e) {
    handleDragStart(e.clientY);
  }

  function onPointerMove(e) {
    handleDragMove(e.clientY);
  }

  function onPointerUp() {
    handleDragEnd();
  }

  function onPointerCancel() {
    handleDragEnd();
  }

  if (!mounted) return null;

  const overlayClass = `csOverlay ${open && !closing ? "isOpen" : "isClosing"}`;
  const sheetStyle = dragY
    ? {
        transform: `translateY(${dragY}px)`,
        transition: "none",
      }
    : undefined;

  return (
    <div
      className={overlayClass}
      style={{ paddingTop: topOffset }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="csSheet"
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        style={sheetStyle}
      >
        <div
          className="csHandleWrap"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="csHandle" />
        </div>

        <div
          className="csTopBar"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="csTopLeft">
            {stack.length ? (
              <button
                type="button"
                className="csBackBtn"
                onClick={goBack}
                aria-label={t("back")}
                title={t("back")}
              >
                <FiChevronLeft className="csBackIcon" />
              </button>
            ) : (
              <div className="csBackStub" />
            )}

            <div className="csTitle">{title}</div>
          </div>

          <button
            type="button"
            className="csIconBtn"
            onClick={onClose}
            aria-label={t("close")}
            title={t("close")}
          >
            <FiX className="csIcon" />
          </button>
        </div>

        <div
          className="csContent"
          style={{ paddingBottom: `calc(24px + env(safe-area-inset-bottom, 0px))` }}
        >
          {loading ? (
            <div className="catGrid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="pCard catCard skeletonCard">
                  <div className="skeletonImg" />
                  <div className="skeletonLine" />
                  <div className="skeletonLine short" />
                  <div className="skeletonBtn" />
                </div>
              ))}
            </div>
          ) : mode === "categories" ? (
            categories?.length ? (
              <div className="csList">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="csRow"
                    onClick={() => openCategory(c)}
                  >
                    <div className="csRowText">
                      <div className="csRowTitle">{c.title}</div>
                    </div>
                    <FiChevronRight className="csChevron" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="csEmpty">{t("empty_categories")}</div>
            )
          ) : products?.length ? (
            <>
    <CatalogProductGrid
      title={t("products_title")}
      products={products}
      onAction={handleProductBlockAction}
      perPage={10}
    />

    <AlternativeProductsBlock
      onOpen={(p) => onOpenProduct?.(p)}
      onAdd={(p) =>
        handleProductBlockAction({
          type: "add_to_cart",
          product_id: p.variant_id || p.id,
          product: { ...p, id: p.variant_id || p.id },
          qty: 1,
        })
      }
    />
  </>
          ) : (
            <div className="csEmpty">{t("empty_products")}</div>
          )}
        </div>
      </div>
    </div>
  );
}